import type { DMAFileTypes } from 'interfaces/ia.interface.ts';
import apiClient, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';

// *: Files API call map. Every call sends the Cognito ID token raw on the
// *: `Authorization` header (no `Bearer` prefix, the apiFetch.ts convention) and
// *: carries an explicit timeout. No URL carries a credential.
// *:
// *:   Documents.tsx / DropFileZone.tsx / FileContainer.tsx / page save handlers
// *:        │
// *:        ▼
// *:   VITE_APP_FILES_API base + Authorization header + timeout
// *:        │                                       │
// *:        ▼ list / upload / link / delete         ▼ download / thumbnail
// *:   GET  /api/files?filter=<encoded>         resolveFileLink(uri)
// *:   POST /api/files/uploadWithoutID            GET <base><uri>?redirect=false -> { url }
// *:   PUT  /api/files/updateID                        │ presigned S3 url (no auth needed)
// *:   PUT  /api/files/delete                          ▼
// *:        │                                     new tab / <img src>
// *:        ▼
// *:   palmettoeoc.api.cloudeoc.com/files

// *: The same 30s transport backstop as apiFetch.ts's `REQUEST_TIMEOUT_MS`, kept
// *: local so this module does not pull the store in through apiFetch.ts.
export const FILES_API_TIMEOUT_MS = 30000;

// *: An upload is one multipart POST. Three minutes leaves room for a multi-MB
// *: photo on a slow field connection, which a 30s limit would cut off.
export const FILES_API_UPLOAD_TIMEOUT_MS = 180000;

// *: Legacy rows predate `cbrnDataFileURIID`. Their object sits in `fallbackbucket`
// *: or, for older uploads, `palmettodev` -- the order the old `<img onError>`
// *: rewrite tried them in.
const LEGACY_FILE_CONTAINERS = ['fallbackbucket', 'palmettodev'];

// *: Intentionally duplicated from apiFetch.ts's redactCredentials() rather than
// *: shared, since this module deliberately does not import apiFetch.ts (that would
// *: pull the store in). Keep both in sync by hand if the redaction shape changes.
const redactedValue = '[redacted]';

export type FilesApiOperation = 'list' | 'upload' | 'link' | 'delete' | 'file-link';

export type FilesApiRequest = { url: string; config: AxiosRequestConfig };

/** A Files API failure that is safe to log or rethrow: it holds no URL, header or token. */
export class FilesApiError extends Error {
  readonly operation: FilesApiOperation;
  readonly status?: number;

  constructor(operation: FilesApiOperation, message: string, status?: number) {
    super(message);
    this.name = 'FilesApiError';
    this.operation = operation;
    this.status = status;
  }
}

function filesApiBaseURL(operation: FilesApiOperation): string {
  const baseURL: string | undefined = import.meta.env.VITE_APP_FILES_API;
  // *: Unset, every URL would become `undefined/api/files`, which the dev server
  // *: answers with index.html -- a list that silently reads as empty.
  if (!baseURL) {
    throw new FilesApiError(
      operation,
      `Files API ${operation} failed: VITE_APP_FILES_API is not set`
    );
  }
  return baseURL;
}

function authorizationHeader(authToken: string, operation: FilesApiOperation) {
  // *: No request goes out without the credential: the Files API could only answer
  // *: 401, and the missing token is the clearer failure to report.
  if (!authToken) {
    throw new FilesApiError(operation, `Files API ${operation} failed: no Cognito ID token`);
  }
  return { Authorization: authToken };
}

// *: Copy of `source` with each named key's value replaced by a placeholder.
// *: Matching is case-insensitive because axios normalizes header casing.
function redactKeys(source: unknown, keys: string[]): unknown {
  if (!source || typeof source !== 'object') return source;
  const targets = keys.map((key) => key.toLowerCase());
  return Object.fromEntries(
    Object.entries(source as Record<string, unknown>).map(([key, value]) => [
      key,
      targets.includes(key.toLowerCase()) ? redactedValue : value,
    ])
  );
}

/**
 * Logs a failed Files API request in a log-safe shape, modeled on apiFetch.ts's
 * `redactCredentials()`: the `Authorization` header and any `access_token` param
 * are stripped, while message, status, method and url stay readable. Returns an
 * error with no URL, header or token in it, so a caller that logs or rethrows it
 * cannot leak the credential.
 */
export function toFilesApiError(operation: FilesApiOperation, error: unknown): FilesApiError {
  if (error instanceof FilesApiError) return error;
  const { message, code, config, response } = (error ?? {}) as Partial<AxiosError>;
  const status = response?.status;
  console.error(`🚀 Files API ${operation} request failed: `, {
    message,
    code,
    status,
    data: response?.data,
    method: config?.method,
    url: config?.url,
    params: redactKeys(config?.params, ['access_token']),
    headers: redactKeys(config?.headers, ['authorization']),
  });
  return new FilesApiError(
    operation,
    status
      ? `Files API ${operation} failed with status ${status}`
      : `Files API ${operation} failed: ${code ?? 'no response'}`,
    status
  );
}

/** The request that lists every file attached to one record (`pvGlobalGroupID`). */
export function listFilesRequest(uuid: string, authToken: string): FilesApiRequest {
  return {
    url: `${filesApiBaseURL('list')}/api/files`,
    config: {
      headers: authorizationHeader(authToken, 'list'),
      // *: Sent through `params` so axios URL-encodes it: the Files API gateway
      // *: answers raw `{}` braces in a query string with a CORS-less 400.
      params: { filter: JSON.stringify({ where: { pvGlobalGroupID: uuid } }) },
      timeout: FILES_API_TIMEOUT_MS,
    },
  };
}

/** The request that uploads one file; the save handler links it to the record later. */
export function uploadFileRequest(authToken: string): FilesApiRequest {
  return {
    url: `${filesApiBaseURL('upload')}/api/files/uploadWithoutID`,
    config: {
      // *: Nothing beyond these two: the gateway's CORS preflight allows no custom
      // *: headers, so the legacy uuid/userid/username headers would block the upload.
      headers: {
        'Content-Type': 'multipart/form-data',
        ...authorizationHeader(authToken, 'upload'),
      },
      timeout: FILES_API_UPLOAD_TIMEOUT_MS,
    },
  };
}

type FileLinkSource = Partial<
  Pick<DMAFileTypes, 'cbrnDataFileName' | 'cbrnDataFileURIID' | 'file_key'>
>;

/**
 * The Files API paths that can serve this file, in the order to try them. The
 * `cbrnDataFileName` check matches every other new-vs-legacy branch in the app, so
 * a row carrying both shapes still takes the new-style path. `preferredURI` -- the
 * path this file's thumbnail already loaded from -- moves to the front.
 */
export function fileLinkURIs(file: FileLinkSource, preferredURI?: string): string[] {
  let uris: string[] = [];
  if (Object.prototype.hasOwnProperty.call(file, 'cbrnDataFileName')) {
    if (file.cbrnDataFileURIID) uris = [file.cbrnDataFileURIID];
  } else if (file.file_key) {
    const fileKey = encodeURIComponent(file.file_key);
    uris = LEGACY_FILE_CONTAINERS.map(
      (container) => `/api/containers/${container}/download/${fileKey}`
    );
  }
  if (!preferredURI || !uris.includes(preferredURI)) return uris;
  return [preferredURI, ...uris.filter((uri) => uri !== preferredURI)];
}

function isHttpsURL(value: unknown): value is string {
  if (typeof value !== 'string') return false;
  try {
    return new URL(value).protocol === 'https:';
  } catch {
    // *: An unparseable value is refused like any other non-https one. Nothing is
    // *: logged here: the value is a signed link, and the caller logs the refusal.
    return false;
  }
}

/**
 * Asks the Files API for a short-lived link to one stored file. `?redirect=false`
 * makes it answer `{ url }` instead of redirecting, because a new tab or an
 * `<img src>` cannot send the Authorization header.
 */
export async function resolveFileLink(
  uri: string,
  authToken: string,
  timeoutMs: number = FILES_API_TIMEOUT_MS
): Promise<string> {
  // *: The stored path is appended to the base, so one that does not start with `/`
  // *: could move the request -- and the token on it -- to another host.
  if (!uri.startsWith('/')) {
    throw new FilesApiError(
      'file-link',
      'Files API file-link failed: the stored path is not a path'
    );
  }
  const url = `${filesApiBaseURL('file-link')}${uri}`;
  const config: AxiosRequestConfig = {
    headers: authorizationHeader(authToken, 'file-link'),
    params: { redirect: 'false' },
    timeout: timeoutMs,
  };

  let response: AxiosResponse<{ url?: unknown } | null>;
  try {
    response = await apiClient.get(url, config);
  } catch (error) {
    throw toFilesApiError('file-link', error);
  }

  const link = response.data?.url;
  // *: The link becomes a navigation or image target, so only https is used. The
  // *: body stays out of the log: the link in it is a credential for that file.
  if (!isHttpsURL(link)) {
    console.error('🚀 Files API file-link response carried no https url: ', {
      status: response.status,
      url,
    });
    throw new FilesApiError(
      'file-link',
      'Files API file-link response carried no https url',
      response.status
    );
  }
  return link;
}

// *: Only the first attempt gets the full transport timeout. A legacy row can carry
// *: two fallback paths, and a first-path timeout at the full budget would hold the
// *: UI for their sum before the caller sees a failure.
const FILES_API_FALLBACK_RETRY_TIMEOUT_MS = 10000;

/** The link for the first of `uris` the Files API resolves; rejects when none resolves. */
export async function resolveFirstFileLink(uris: string[], authToken: string): Promise<string> {
  let lastError: unknown = new FilesApiError(
    'file-link',
    'Files API file-link failed: the file has no stored path'
  );
  for (let i = 0; i < uris.length; i++) {
    try {
      const timeoutMs = i === 0 ? FILES_API_TIMEOUT_MS : FILES_API_FALLBACK_RETRY_TIMEOUT_MS;
      return await resolveFileLink(uris[i], authToken, timeoutMs);
    } catch (error) {
      // *: resolveFileLink logged the request failure; the next path is the
      // *: fallback copy. The last failure is what the caller reports.
      lastError = error;
    }
  }
  throw lastError;
}

function jsonRequestConfig(authToken: string, operation: FilesApiOperation): AxiosRequestConfig {
  return {
    headers: { 'Content-Type': 'application/json', ...authorizationHeader(authToken, operation) },
    timeout: FILES_API_TIMEOUT_MS,
  };
}

type AddOrDeleteFilesFuncProps = {
  filesToAdd: DMAFileTypes[];
  filesToDelete: DMAFileTypes[];
  uuid: string;
  authToken: string;
};
export const addOrDeleteFilesFunc = async ({
  filesToAdd = [],
  filesToDelete = [],
  uuid,
  authToken,
}: AddOrDeleteFilesFuncProps) => {
  // *: A save that adds and removes no file never depends on the Files API.
  if (!filesToAdd.length && !filesToDelete.length) return 0;

  const calls: Promise<unknown>[] = [];
  for (let i = 0; i < filesToAdd.length; i++) {
    const url = `${filesApiBaseURL('link')}/api/files/updateID`;
    const obj = {
      pvDataID: filesToAdd[i].pvDataID,
      pvGlobalGroupID: uuid,
    };
    calls.push(
      apiClient.put(url, obj, jsonRequestConfig(authToken, 'link')).catch((error) => {
        throw toFilesApiError('link', error);
      })
    );
  }

  for (let i = 0; i < filesToDelete.length; i++) {
    const url = `${filesApiBaseURL('delete')}/api/files/delete`;
    const obj = {
      globalid: filesToDelete[i].pvGlobalID,
    };
    calls.push(
      apiClient.put(url, obj, jsonRequestConfig(authToken, 'delete')).catch((error) => {
        throw toFilesApiError('delete', error);
      })
    );
  }

  // *: Promise.all rejects on the first failure but leaves the rest running --
  // *: allSettled waits for every call so a partial completion (some links/deletes
  // *: already landed on the Files API) is visible in the log, not just the first error.
  const results = await Promise.allSettled(calls);
  const rejected = results.filter(
    (result): result is PromiseRejectedResult => result.status === 'rejected'
  );
  if (rejected.length > 0) {
    console.error(
      `🚀 Files API add/delete partially failed: ${results.length - rejected.length} of ${
        results.length
      } file operations succeeded`
    );
    throw rejected[0].reason;
  }

  return filesToAdd.length - filesToDelete.length;
};
